import React, {useEffect, useMemo, useRef} from "react";
import {useProductListContext} from "../ProductListProvider/ProductListProvider";
import {useTranslation} from "react-i18next";
import styles from "./ProductList.module.scss";
import itemsListStyles from "../../ProductSeries/ItemsList.module.scss";
import ItemCard, {scrollInfoStorage} from "../../ProductSeries/ItemCard";
import {getShopId} from "../../lib/getShopId";
import PatternInterruption from "./PatternInterruption";
import {Tracking} from "../../../tracking/tracking";
import {useMediaQuery} from "react-responsive";
import {IItemContent, ProductListFilterItemValueType} from "@connox/typescript-library";
import isSameObject from "../../../helpers/isSameObject";

/**
 * The component showing up the product tiles. If no items are
 * present and still loading, a loading spinner is shown.<br/>
 * If not loading there will be a note saying 'No articles present'
 * @constructor
 */
const ListItems: React.FC = () => {
  const {state} = useProductListContext();
  const {t} = useTranslation();
  const isMobile = useMediaQuery({query: "(max-width: 480px)"});

  if (!state.items.length) {
    if (state.loading) {
      return (
        <div className={styles.suspensesContainer}>
          <div className={styles.spinner}>
            <div className="swiper-lazy-preloader" />
          </div>
        </div>
      );
    } else {
      return (
        <div className={[styles.suspensesContainer, styles.viewMoreButtonContainer].join(" ")}>
          {t("Leider haben wir keine Artikel, die zu Ihrer Auswahl passen. Bitte passen Sie die Filter an.")}
        </div>
      );
    }
  }

  return (
    <div className={[itemsListStyles.wrapper, styles.listItemsContainer].join(" ")}>
      {!isMobile && <div className={styles.totalItemCount}>{t("{{quantity}} Artikel", {quantity: state.total})}</div>}
      <ItemCards />
    </div>
  );
};

export default ListItems;

const currentHeadline = document.body.querySelector<HTMLElement>(".product-list-react")?.dataset.headline;

const ItemCards: React.FC = () => {
  const {state} = useProductListContext();
  const oldListRef = useRef<number[]>([]);
  const oldFiltersRef = useRef<{[p: string]: ProductListFilterItemValueType}>({});

  const Pattern = useMemo(() => <PatternInterruption />, []);
  const scrollInfo = scrollInfoStorage.read(document.body.id);

  const isItemClicked = useRef(false);

  useEffect(() => {
    const saveScrollPosition = () => {
      if (!isItemClicked.current) {
        scrollInfoStorage.write(document.body.id, {itemId: null, position: window.scrollY});
      }
    };
    window.addEventListener("beforeunload", saveScrollPosition);

    if (scrollInfo && scrollInfo.itemId === null) {
      const {position} = scrollInfo;
      if (typeof window.scrollTo === "function") {
        window.scrollTo({left: 0, top: position});
      } else {
        window.scroll(0, position);
      }
      scrollInfoStorage.remove(document.body.id);
    }
  }, []);

  useEffect(() => {
    const isSameFilterSet = isSameObject<{[p: string]: ProductListFilterItemValueType}>(
      state.filters,
      oldFiltersRef.current,
    );
    const diffItems: {index: number; item: IItemContent}[] = [];
    state.items.forEach((itm, index) => {
      // if state.items has new item on particular index, or it's the same by coincidence from a new filter setting
      if (oldListRef.current[index] !== itm.itemId || !isSameFilterSet) {
        diffItems.push({item: itm, index});
      }
    });

    Tracking.trackProductImpressions(diffItems, currentHeadline);

    oldListRef.current = state.items.map((itm) => itm.itemId);
    oldFiltersRef.current = state.filters;
  }, [state.items]);

  const elementsArray = state.items.map((item, index) => {
    const onClick = () => {
      scrollInfoStorage.write(document.body.id, {itemId: item.itemId, position: window.scrollY});
      isItemClicked.current = true;

      Tracking.trackImpressionClicked(
        item,
        index,
        document.body.querySelector<HTMLElement>(".product-list-react")?.dataset.headline,
      );
    };

    return <ItemCard key={index} item={item} onClick={onClick} swipeable={false} scrollInfo={scrollInfo} />;
  });
  if (getShopId() === 100001 && state.items.length >= 8) {
    elementsArray.splice(8, 0, <React.Fragment key="pattern-interruption">{Pattern}</React.Fragment>);
  }

  return <>{elementsArray}</>;
};
